import { useState, useEffect, useRef, lazy, Suspense } from "react";
import type { Session } from "@supabase/supabase-js";
import Sidebar from "./components/layout/Sidebar";
import DashboardPage from "./components/dashboard/DashboardPage";
import BudgetPage from "./components/budget/BudgetPage";
import PortfolioPage from "./components/portfolio/PortfolioPage";
import LoansPage from "./components/loans/LoansPage";
import TransfersPage from "./components/transfers/TransfersPage";
import PropertyDetailPage from "./components/portfolio/PropertyDetailPage";
import { loadProperties, saveProperties, loadStructures, saveStructures, loadProfile, saveProfile, loadBudget, saveBudget } from "./lib/storage";
import {
  fetchAllData,
  upsertProperty, upsertProperties, deleteSupabaseProperty,
  upsertStructure, upsertStructures, deleteSupabaseStructure,
  upsertProfile, fetchProfile,
  upsertBudget,
  upsertAppSettings,
} from "./lib/supabaseStorage";
import type { PropertyRecord } from "./types/portfolio";
import { backfillMissingCoordinates } from "./lib/geocode";
import { maybeAppendSnapshot } from "./lib/historySnapshot";
import type { Structure, InvestorProfile } from "./types/entities";
import type { BudgetSettings, BudgetCategory, IncomeRow } from "./types/budget";
import type { ExtractedBudget } from "./components/import/ImportWizard";
import ProfilePage from "./components/profile/ProfilePage";
import SettingsPage, { loadAppSettings, saveAppSettings, type AppSettings } from "./components/settings/SettingsPage";
import AIChatDrawer from "./components/ai/AIChatDrawer";
import { buildPortfolioContext } from "./lib/buildPortfolioContext";
import { computeBucketAllocations } from "./lib/calculations";
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import EmptyState from "./components/onboarding/EmptyState";

import AuthPage from "./components/auth/AuthPage";
import SetPasswordPage from "./components/auth/SetPasswordPage";
import UserMenu from "./components/auth/UserMenu";
import ImportWizard from "./components/import/ImportWizard";
import BottomNav from "./components/layout/BottomNav";
import UpgradeModal from "./components/billing/UpgradeModal";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import { useSubscription } from "./hooks/useSubscription";
import { supabase, isSupabaseEnabled } from "./lib/supabase";
import { properties as seedProperties, structures as seedStructures, budgetSettings as seedBudgetSettings, budgetCategories as seedBudgetCategories } from "./data/seed";

// Heavy pages — loaded on demand
const SimulatorPage = lazy(() => import("./components/simulator/SimulatorPage"));
const StrategyPage  = lazy(() => import("./components/strategy/StrategyPage"));
const ReportsPage   = lazy(() => import("./components/reports/ReportsPage"));
const TaxPage       = lazy(() => import("./components/tax/TaxPage"));
const BillingPage   = lazy(() => import("./components/billing/BillingPage"));
const AdminPage     = lazy(() => import("./components/admin/AdminPage"));
const LandingPage   = lazy(() => import("./pages/LandingPage"));

const PageSpinner = (
  <div className="flex items-center justify-center py-20">
    <div className="h-5 w-5 rounded-full border-2 border-neutral-200 border-t-[#C47A2F] animate-spin" />
  </div>
);

type Page =
  | "dashboard"
  | "profile"
  | "budget"
  | "portfolio"
  | "property-detail"
  | "loans"
  | "transfers"
  | "simulator"
  | "strategy"
  | "reports"
  | "tax"
  | "billing"
  | "settings"
  | "admin";

type ScenarioDraftProperty = {
  nickname: string;
  address: string;
  structureId: string;
  purchasePrice: number;
  currentValue: number;
  weeklyRent: number;
  loanBalance: number;
  lender: string;
  monthlyExpenses: number;
  rate: number;
  notes: string;
  acquisitionCosts?: PropertyRecord['acquisitionCosts'];
  expenseRows?: PropertyRecord['expenseRows'];
  holdingBuffer?: number;
};

function mapScenarioDraftToProperty(
  draft: ScenarioDraftProperty,
  _index: number
): PropertyRecord {
  return {
    id: crypto.randomUUID(),
    nickname: draft.nickname,
    address: draft.address,
    suburb: "Scenario Suburb",
    state: "QLD",
    postcode: "4000",
    assetClass: "Residential",
    propertyType: "House",
    purchaseDate: "2026-03-24",
    purchasePrice: draft.purchasePrice,
    buyCosts: 0,
    legalBuyCosts: 2500,
    currentValue: draft.currentValue,
    weeklyRent: draft.weeklyRent,
    loanBalance: draft.loanBalance,
    structureId: draft.structureId,
    owner: draft.structureId,
    lender: draft.lender,
    loanAccountNumber: "SCENARIO-DRAFT",
    offsetAccount: "",
    loanStartDate: "2026-03-24",
    loanTermYears: 30,
    ioTermYears: 0,
    rate: draft.rate,
    repaymentType: "P&I",
    rateType: "Variable",
    monthlyExpenses: Math.round(draft.monthlyExpenses * 100) / 100,
    annualRates: 1800,
    annualInsurance: 1500,
    annualStrata: 0,
    annualLandTax: 0,
    annualMaintenance: 1000,
    annualPropertyManagement: 1800,
    personalTopUp: 0,
    estimatedSellingCostsPct: 2.5,
    customExpenses: [],
    customTabs: [],
    notes: draft.notes,
    acquisitionCosts: draft.acquisitionCosts,
    expenseRows: draft.expenseRows,
    holdingBuffer: draft.holdingBuffer ?? 0,
    holdingBufferStartDate: draft.holdingBuffer ? new Date().toISOString().slice(0, 10) : undefined,
  };
}

export default function App() {
  // ─── Auth (Supabase Phase 2) ────────────────────────────────────────────────
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(isSupabaseEnabled());
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const supabaseDataLoaded = useRef(false);

  useEffect(() => {
    if (!isSupabaseEnabled() || !supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
        setSession(s);
        setSessionLoading(false);
        return;
      }
      setSession(s);
      setSessionLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ─── Supabase data load (once per session) ─────────────────────────────────
  useEffect(() => {
    if (!isSupabaseEnabled() || !session || supabaseDataLoaded.current) return;
    supabaseDataLoaded.current = true;

    // Guard against cross-user localStorage bleed: if the stored userId differs
    // from the newly signed-in user, wipe all ppa: data before loading.
    const storedUserId = localStorage.getItem("ppa:userId");
    if (storedUserId && storedUserId !== session.user.id) {
      ["ppa:properties", "ppa:structures", "ppa:profile", "ppa:budget", "ppa:onboarded", "ppa:settings"].forEach((k) => localStorage.removeItem(k));
    }
    localStorage.setItem("ppa:userId", session.user.id);

    fetchAllData(session.user.id).then(({ properties, structures, profile: fetchedProfile, budget: fetchedBudget, settings: fetchedSettings }) => {
      // N1 — first-load backfill: seed empty valueHistory from purchase data
      // for properties that pre-date the value-history feature. Runs once
      // per property (only when valueHistory is empty); subsequent edits
      // append via maybeAppendSnapshot in updateProperty.
      for (const p of properties) {
        if ((!p.valueHistory || p.valueHistory.length === 0) && p.purchasePrice > 0 && p.purchaseDate) {
          p.valueHistory = [{ id: crypto.randomUUID(), date: p.purchaseDate, value: p.purchasePrice }];
          // Push the seeded history back to Supabase so future loads skip the seed.
          void upsertProperty(p, session.user.id);
        }
      }

      // ── Properties: per-row dirty reconcile ──
      const dirtyPropIds = getDirtyIds("ppa:properties:dirty");
      if (properties.length > 0) {
        if (dirtyPropIds.length > 0) {
          const dirtySet = new Set(dirtyPropIds);
          const localProps = loadProperties();
          const localById = new Map(localProps.map((p) => [p.id, p]));
          // Cloud properties NOT dirty → use cloud. Dirty → use local version.
          const merged = properties.filter((p) => !dirtySet.has(p.id));
          for (const id of dirtyPropIds) {
            const local = localById.get(id);
            if (local) {
              merged.push(local);
              // Retry pushing dirty property to cloud
              void upsertProperty(local, session.user.id).then((r) =>
                handleSaveResult("ppa:properties:dirty", id, r, "property")
              );
            }
          }
          setPortfolioProperties(merged);
          saveProperties(merged);
        } else {
          setPortfolioProperties(properties);
          saveProperties(properties);
        }
        // Backfill lat/lng for any properties missing coordinates (runs in background, 1.1s/property)
        void backfillMissingCoordinates(properties, (id, latitude, longitude) => {
          setPortfolioProperties(prev => {
            const updated = prev.map(p => p.id === id ? { ...p, latitude, longitude } : p);
            const prop = updated.find(p => p.id === id);
            if (prop && isSupabaseEnabled()) {
              addDirtyId("ppa:properties:dirty", id);
              void upsertProperty(prop, session.user.id).then((r) =>
                handleSaveResult("ppa:properties:dirty", id, r, "property")
              );
            }
            return updated;
          });
        });
      } else {
        // Auto-sync: Supabase has no properties — check if localStorage has some and push them up silently.
        try {
          const raw = localStorage.getItem("ppa:properties");
          const localProps: PropertyRecord[] = raw ? (JSON.parse(raw) as PropertyRecord[]) : [];
          if (localProps.length > 0) {
            void upsertProperties(localProps, session.user.id).then(() => {
              console.log(`Auto-synced ${localProps.length} properties to Supabase`);
            }).catch((err) => showGlobalSaveError(`Failed to sync properties — ${(err as Error).message}`));
          }
        } catch {}
      }
      // ── Structures: per-row dirty reconcile ──
      const dirtyStructIds = getDirtyIds("ppa:structures:dirty");
      if (structures.length > 0) {
        if (dirtyStructIds.length > 0) {
          const dirtySet = new Set(dirtyStructIds);
          const localStructs = loadStructures();
          const localById = new Map(localStructs.map((s) => [s.id, s]));
          const merged = structures.filter((s) => !dirtySet.has(s.id));
          for (const id of dirtyStructIds) {
            const local = localById.get(id);
            if (local) {
              merged.push(local);
              void upsertStructure(local, session.user.id).then((r) =>
                handleSaveResult("ppa:structures:dirty", id, r, "structure")
              );
            }
          }
          setPortfolioStructures(merged);
          saveStructures(merged);
        } else {
          setPortfolioStructures(structures);
          saveStructures(structures);
        }
      }
      // ── Profile: dirty flag reconcile ──
      if (fetchedProfile) {
        if (isDirtyFlag("ppa:profile:dirty")) {
          void upsertProfile(profile, session.user.id).then((r) =>
            handleSaveResult("ppa:profile:dirty", null, r, "profile")
          );
        } else {
          setProfile(fetchedProfile);
          saveProfile(fetchedProfile);
        }
      }
      if (fetchedBudget) {
        const isDirty = localStorage.getItem("ppa:budget:dirty") === "true";
        if (isDirty) {
          // localStorage has unsaved changes from a failed Supabase write — push local to cloud
          console.warn("[App] Budget dirty flag set — pushing localStorage to Supabase instead of overwriting");
          void upsertBudget({ settings: budgetSettings, categories: budgetCategories }, session.user.id).then((result) => {
            if (result.ok) {
              localStorage.setItem("ppa:budget:dirty", "false");
              const now = new Date().toISOString();
              localStorage.setItem("ppa:budget:lastSaved", now);
              setBudgetLastSaved(now);
            }
          });
        } else {
          setBudgetSettings(fetchedBudget.settings);
          setBudgetCategories(fetchedBudget.categories);
          saveBudget(fetchedBudget);
        }
      }
      if (fetchedSettings) {
        if (isDirtyFlag("ppa:appSettings:dirty")) {
          void upsertAppSettings(appSettings, session.user.id).then((r) =>
            handleSaveResult("ppa:appSettings:dirty", null, r, "settings")
          );
        } else {
          setAppSettings((prev) => ({ ...prev, ...fetchedSettings }));
        }
      }

      // ── Phase 6a: seed property bucket if missing (runs once, after both budget + settings loaded) ──
      if (fetchedBudget && fetchedSettings && !(fetchedSettings.propertyBucketSeeded)) {
        const budgetToCheck = fetchedBudget.settings;
        if (!budgetToCheck.buckets.some((b) => b.type === "property")) {
          const override = (fetchedSettings as Record<string, unknown>).personalContributionOverride as number | undefined;
          const investBucket = budgetToCheck.buckets.find((b) => b.type === "investment");
          const amount = override ?? investBucket?.monthlyAmount ?? 0;
          const newBuckets = [...budgetToCheck.buckets, {
            id: crypto.randomUUID(),
            name: "Property Portfolio",
            type: "property" as const,
            locked: true,
            monthlyAmount: amount,
            description: "Money allocated from your monthly surplus toward your property portfolio",
          }];
          // Demote investment bucket if we used its amount
          const finalBuckets = (!override && investBucket)
            ? newBuckets.map((b) => b.id === investBucket.id ? { ...b, type: "custom" as const } : b)
            : newBuckets;
          const newSettings = { ...budgetToCheck, buckets: finalBuckets };
          setBudgetSettings(newSettings);
          setBudgetCategories(fetchedBudget.categories);
          saveBudget({ settings: newSettings, categories: fetchedBudget.categories });
          void upsertBudget({ settings: newSettings, categories: fetchedBudget.categories }, session.user.id);
          // Set seeded flag + clear override
          const updatedAppSettings = { ...appSettings, ...(fetchedSettings as Partial<AppSettings>), propertyBucketSeeded: true };
          setAppSettings(updatedAppSettings);
          saveAppSettings(updatedAppSettings);
          void upsertAppSettings(updatedAppSettings, session.user.id);
          console.log("[App] Property bucket seeded:", amount, "/mo");
        }
      }

      // Check if this user is viewing a shared portfolio — if so, fetch the owner's profile for goal fields
      if (supabase) {
        supabase
          .from("portfolio_shares")
          .select("owner_user_id")
          .eq("shared_with_user_id", session.user.id)
          .eq("status", "accepted")
          .limit(1)
          .maybeSingle()
          .then(({ data: shareRow }) => {
            if (shareRow?.owner_user_id) {
              fetchProfile(shareRow.owner_user_id).then((ownerP) => {
                if (ownerP) setOwnerProfile(ownerP);
              });
            }
          });
      }
      // Auto-onboarded: if user has data in Supabase, they're not a new user.
      // Prevents OnboardingWizard from firing after localStorage clears
      // (cross-user wipe, fresh browser, password recovery, etc.)
      const hasData = properties.length > 0 || (fetchedProfile?.annualPreTaxIncome ?? 0) > 0;
      if (hasData) {
        setOnboarded(true);
        localStorage.setItem("ppa:onboarded", "true");
      }
      setDataLoaded(true);
    });
  }, [session]);

  const [onboarded, setOnboarded] = useState(
    () => localStorage.getItem("ppa:onboarded") === "true"
  );
  const [current, setCurrent] = useState<Page>("dashboard");

  // Handle Stripe redirect back to the app — detect billing_status query param and navigate to billing page.
  // The app has no URL router so Stripe's success/cancel URLs use query params on the root URL instead of paths.
  // On success, refresh the subscription tier — the webhook may have already updated user_profiles.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingStatus = params.get("billing_status");
    if (billingStatus === "success" || billingStatus === "cancelled") {
      setCurrent("billing");
      window.history.replaceState({}, "", "/");
      if (billingStatus === "success") {
        // Small delay to allow the webhook to process before re-fetching
        setTimeout(() => void refreshSubscription(), 2000);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Referral code capture from URL ────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (!refCode) return;
    localStorage.setItem("ausprop_referral_code", refCode.toUpperCase().trim());
    params.delete("ref");
    const remaining = params.toString();
    window.history.replaceState({}, "", remaining ? `?${remaining}` : "/");
  }, []);

  // ─── Referral record insertion on sign-in ──────────────────────────────────
  useEffect(() => {
    if (!session || !isSupabaseEnabled() || !supabase) return;
    const refCode = localStorage.getItem("ausprop_referral_code");
    if (!refCode) return;
    // Prevent double-processing for the same user session
    if (localStorage.getItem("ausprop_referral_processed") === session.user.id) return;

    async function recordReferral() {
      if (!supabase || !session) return;
      try {
        const { data: referrerId } = await supabase.rpc("get_user_id_by_referral_code", { p_code: refCode });
        if (!referrerId || referrerId === session.user.id) {
          localStorage.removeItem("ausprop_referral_code");
          return;
        }
        await supabase.from("referrals").insert({
          referrer_user_id: referrerId,
          referee_email: session.user.email ?? null,
          referee_user_id: session.user.id,
          status: "pending",
        });
        localStorage.setItem("ausprop_referral_processed", session.user.id);
        localStorage.removeItem("ausprop_referral_code");
        console.log("[Referral] recorded referral from code:", refCode);
      } catch (e) {
        console.error("[Referral] error recording referral:", e);
      }
    }
    void recordReferral();
   
  }, [session]);

  // ─── Share token acceptance ─────────────────────────────────────────────────
  const [shareAcceptedBanner, setShareAcceptedBanner] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseEnabled() || !supabase) return;
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get("share_token");
    if (!shareToken) return;

    // Remove token from URL immediately regardless of auth state
    window.history.replaceState({}, "", "/");

    async function acceptShare(token: string) {
      if (!supabase) return;
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        // User not logged in — redirect to auth, the token is already cleared from URL
        // They will need to re-accept after logging in (limitation of no-router architecture)
        return;
      }

      // Look up the pending share by token
      const { data: share, error } = await supabase
        .from("portfolio_shares")
        .select("id, owner_user_id, status")
        .eq("invite_token", token)
        .eq("status", "pending")
        .maybeSingle();

      if (error || !share) return;

      // Accept: set shared_with_user_id + status
      const { error: updateError } = await supabase
        .from("portfolio_shares")
        .update({
          shared_with_user_id: currentSession.user.id,
          status: "accepted",
          accepted_at: new Date().toISOString(),
        })
        .eq("id", share.id);

      if (!updateError) {
        // Try to get owner name for the banner
        const { data: ownerProfile } = await supabase
          .from("profiles")
          .select("preferred_name, full_legal_name")
          .eq("user_id", share.owner_user_id)
          .maybeSingle();
        const ownerName = ownerProfile?.preferred_name ?? ownerProfile?.full_legal_name ?? "another investor";
        setShareAcceptedBanner(`You now have access to ${ownerName}'s portfolio`);
        setTimeout(() => setShareAcceptedBanner(null), 6000);
      }
    }

    void acceptShare(shareToken);
   
  }, []);
  // When Supabase is enabled, don't fall back to seed data — start empty and wait for fetchAllData.
  // When Supabase is not enabled (Phase 1), use the seed fallback as before.
  const [portfolioProperties, setPortfolioProperties] = useState<PropertyRecord[]>(() => {
    if (isSupabaseEnabled()) {
      try {
        const raw = localStorage.getItem("ppa:properties");
        if (raw) return JSON.parse(raw) as PropertyRecord[];
      } catch {}
      return [];
    }
    return loadProperties();
  });
  const [portfolioStructures, setPortfolioStructures] = useState<Structure[]>(() => {
    if (isSupabaseEnabled()) {
      try {
        const raw = localStorage.getItem("ppa:structures");
        if (raw) return JSON.parse(raw) as Structure[];
      } catch {}
      return [];
    }
    return loadStructures();
  });
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedPropertyForSale, setSelectedPropertyForSale] = useState<string | null>(null);
  const [profile, setProfile] = useState<InvestorProfile>(loadProfile);
  const [ownerProfile, setOwnerProfile] = useState<InvestorProfile | null>(null);

  // Effective profile for goal/strategy fields — uses portfolio owner's goal fields if viewing shared portfolio,
  // but keeps the current user's personal fields (income, tax, household members)
  const effectiveGoalProfile: InvestorProfile = ownerProfile
    ? {
        ...profile,
        passiveIncomeTarget: ownerProfile.passiveIncomeTarget,
        portfolioTargetDate: ownerProfile.portfolioTargetDate,
        retirementAge: ownerProfile.retirementAge,
        retirementIncomeTarget: ownerProfile.retirementIncomeTarget,
        goalFeasibility: ownerProfile.goalFeasibility,
        goalIsCustom: ownerProfile.goalIsCustom,
        visionStatement: ownerProfile.visionStatement,
        primaryStrategy: ownerProfile.primaryStrategy,
        secondaryStrategy: ownerProfile.secondaryStrategy,
        portfolioPhase: ownerProfile.portfolioPhase,
        phaseOverride: ownerProfile.phaseOverride,
        currentConstraint: ownerProfile.currentConstraint,
        portfolioValueTarget: ownerProfile.portfolioValueTarget,
        strategyTone: ownerProfile.strategyTone,
      }
    : profile;

  const {
    tier: subscriptionTier,
    isFree: _isFree,
    isInvestor: _isInvestor,
    isInTrial,
    trialExpired: _trialExpired,
    trialDaysLeft,
    hasFullAccess,
    isReadOnly,
    loading: subscriptionLoading,
    refresh: refreshSubscription,
  } = useSubscription(session?.user.id);

  const [appSettings, setAppSettings] = useState<AppSettings>(loadAppSettings);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [importWizardMode, setImportWizardMode] = useState<'replace' | 'merge'>('replace');
  const [cloudSyncBanner, setCloudSyncBanner] = useState(false);
  const [budgetSavedBanner, setBudgetSavedBanner] = useState(false);
  const [budgetSaveError, setBudgetSaveError] = useState<string | null>(null);
  const [budgetLastSaved, setBudgetLastSaved] = useState<string | null>(() => localStorage.getItem("ppa:budget:lastSaved"));
  const budgetSaveDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const budgetSaveBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModalReason, setUpgradeModalReason] = useState<"property_limit" | "ai_feature" | "trial_expired">("property_limit");
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [globalSaveError, setGlobalSaveError] = useState<string | null>(null);
  const globalSaveErrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Dirty flag helpers (idempotent read-modify-write) ─────────────────────
  function addDirtyId(key: string, id: string | null) {
    if (!id) { localStorage.setItem(key, "true"); return; }
    const raw = localStorage.getItem(key);
    const set: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    if (!set.includes(id)) { set.push(id); localStorage.setItem(key, JSON.stringify(set)); }
  }
  function removeDirtyId(key: string, id: string | null) {
    if (!id) { localStorage.setItem(key, "false"); return; }
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const set = (JSON.parse(raw) as string[]).filter((x) => x !== id);
    localStorage.setItem(key, JSON.stringify(set));
  }
  function getDirtyIds(key: string): string[] {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  }
  function isDirtyFlag(key: string): boolean {
    return localStorage.getItem(key) === "true";
  }

  function showGlobalSaveError(msg: string) {
    setGlobalSaveError(msg);
    if (globalSaveErrorTimer.current) clearTimeout(globalSaveErrorTimer.current);
    globalSaveErrorTimer.current = setTimeout(() => setGlobalSaveError(null), 15000);
  }

  function handleSaveResult(dirtyKey: string, dirtyId: string | null, result: { ok: boolean; error?: string }, label: string) {
    if (result.ok) {
      removeDirtyId(dirtyKey, dirtyId);
      // Clear error if it was for this data type
      if (globalSaveError?.includes(label)) setGlobalSaveError(null);
    } else {
      showGlobalSaveError(`Failed to save ${label} — changes saved locally and will retry`);
    }
  }

  function openUpgradeModal(reason: "property_limit" | "ai_feature" | "trial_expired") {
    setUpgradeModalReason(reason);
    setUpgradeModalOpen(true);
  }
  // Auto-open upgrade modal when a trial-expired user loads the app
  useEffect(() => {
    if (!subscriptionLoading && isReadOnly && session) {
      openUpgradeModal("trial_expired");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionLoading, isReadOnly]);

  // Direct Stripe checkout for expired trial banner CTA
  async function handleDirectCheckout() {
    if (!isSupabaseEnabled() || !supabase || !session?.user.id) {
      setCurrent("billing" as Parameters<typeof setCurrent>[0]);
      return;
    }
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "stripe-checkout",
        { body: { priceId: "price_1TII34AEhiPAT6YGrre9u6oC", userId: session.user.id } }
      );
      if (invokeError) throw new Error(invokeError.message);
      const url = (data as { url?: string })?.url;
      if (url) window.location.href = url;
      else setCurrent("billing" as Parameters<typeof setCurrent>[0]);
    } catch {
      setCurrent("billing" as Parameters<typeof setCurrent>[0]);
    }
  }

  const [simulatorDefaultTab, setSimulatorDefaultTab] = useState<string | undefined>(undefined);

  const [budgetSettings, setBudgetSettings] = useState<BudgetSettings>(
    () => loadBudget().settings
  );
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>(
    () => loadBudget().categories
  );

  useEffect(() => { if (!isDemoMode) saveProperties(portfolioProperties); }, [portfolioProperties, isDemoMode]);
  useEffect(() => { if (!isDemoMode) saveStructures(portfolioStructures); }, [portfolioStructures, isDemoMode]);
  useEffect(() => { saveProfile(profile); }, [profile]);
  useEffect(() => {
    saveBudget({ settings: budgetSettings, categories: budgetCategories }); // immediate localStorage
    if (isSupabaseEnabled() && session) {
      localStorage.setItem("ppa:budget:dirty", "true");
      if (budgetSaveDebounce.current) clearTimeout(budgetSaveDebounce.current);
      budgetSaveDebounce.current = setTimeout(async () => {
        const result = await upsertBudget({ settings: budgetSettings, categories: budgetCategories }, session.user.id);
        if (result.ok) {
          localStorage.setItem("ppa:budget:dirty", "false");
          const now = new Date().toISOString();
          localStorage.setItem("ppa:budget:lastSaved", now);
          setBudgetLastSaved(now);
          setBudgetSaveError(null);
          setBudgetSavedBanner(true);
          if (budgetSaveBannerTimer.current) clearTimeout(budgetSaveBannerTimer.current);
          budgetSaveBannerTimer.current = setTimeout(() => setBudgetSavedBanner(false), 2000);
        } else {
          setBudgetSaveError(result.error ?? "Failed to save budget to cloud");
          setBudgetSavedBanner(false);
        }
      }, 1500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetSettings, budgetCategories]);

  function updateProfile(p: InvestorProfile) {
    setProfile(p);
    if (isSupabaseEnabled() && session) {
      addDirtyId("ppa:profile:dirty", null);
      void upsertProfile(p, session.user.id).then((r) => handleSaveResult("ppa:profile:dirty", null, r, "profile"));
    }
  }

  function handleOnboardingComplete(data: { profile: InvestorProfile; property?: PropertyRecord }) {
    setProfile(data.profile);
    setPortfolioProperties(data.property ? [data.property] : []);
    setPortfolioStructures([]);
    setBudgetSettings(seedBudgetSettings);
    setBudgetCategories(seedBudgetCategories);
    setSelectedPropertyId(null);
    setSelectedPropertyForSale(null);
    setOnboarded(true);
    if (isSupabaseEnabled() && session) {
      addDirtyId("ppa:profile:dirty", null);
      void upsertProfile(data.profile, session.user.id).then((r) => handleSaveResult("ppa:profile:dirty", null, r, "profile"));
      if (data.property) {
        addDirtyId("ppa:properties:dirty", data.property.id);
        void upsertProperty(data.property, session.user.id).then((r) => handleSaveResult("ppa:properties:dirty", data.property!.id, r, "property"));
      }
    }
  }

  function handleOnboardingSkip() {
    setOnboarded(true);
  }

  // ─── EmptyState handlers (Phase 2 new-user flow) ───────────────────────────
  function handleEmptyStateImport() {
    setImportWizardOpen(true);
    setOnboarded(true);
    localStorage.setItem("ppa:onboarded", "true");
  }

  function handleEmptyStateAddManually() {
    setOnboarded(true);
    localStorage.setItem("ppa:onboarded", "true");
    setCurrent("portfolio");
  }

  function handleEmptyStateLoadDemo() {
    setIsDemoMode(true);
    setPortfolioProperties(seedProperties);
    setPortfolioStructures(seedStructures);
    setBudgetSettings(seedBudgetSettings);
    setBudgetCategories(seedBudgetCategories);
    setOnboarded(true);
  }

  function exitDemoMode() {
    setIsDemoMode(false);
    setPortfolioProperties([]);
    setPortfolioStructures([]);
    setBudgetSettings(loadBudget().settings);
    setBudgetCategories(loadBudget().categories);

    // Re-fetch real data from Supabase if available
    if (isSupabaseEnabled() && session) {
      supabaseDataLoaded.current = false;
      fetchAllData(session.user.id).then(({ properties, structures, profile: fetchedProfile, budget: fetchedBudget }) => {
        supabaseDataLoaded.current = true;
        if (properties.length > 0) {
          setPortfolioProperties(properties);
          saveProperties(properties);
        }
        if (structures.length > 0) {
          setPortfolioStructures(structures);
          saveStructures(structures);
        }
        if (fetchedProfile) {
          setProfile(fetchedProfile);
          saveProfile(fetchedProfile);
        }
        if (fetchedBudget) {
          setBudgetSettings(fetchedBudget.settings);
          setBudgetCategories(fetchedBudget.categories);
        }
      });
    } else {
      // Fallback to localStorage
      setPortfolioProperties(loadProperties());
      setPortfolioStructures(loadStructures());
    }
  }

  function updateBudgetSettings(s: BudgetSettings) { setBudgetSettings(s); }
  function updateBudgetCategories(c: BudgetCategory[]) { setBudgetCategories(c); }

  function budgetFreqToMonthly(amount: number, freq: string): number {
    if (freq === "W" || freq === "Weekly")              return (amount * 52) / 12;
    if (freq === "F" || freq === "Fortnightly")         return (amount * 26) / 12;
    if (freq === "Q" || freq === "Quarterly")           return amount / 3;
    if (freq === "Y" || freq === "Annual" || freq === "Yearly") return amount / 12;
    return amount;
  }
  const computedMonthlyIncome = (budgetSettings.incomeRows ?? []).reduce(
    (sum, r) => sum + budgetFreqToMonthly(r.amount, r.frequency), 0
  );
  const computedMonthlyExpenses = budgetCategories.reduce(
    (sum, c) => sum + budgetFreqToMonthly(c.budget, c.frequency), 0
  );
  const _rawSurplus = Math.max(0, computedMonthlyIncome - computedMonthlyExpenses);
  const _bucketAllocations = computeBucketAllocations(budgetSettings.buckets ?? [], _rawSurplus);
  const _totalFunded = _bucketAllocations.reduce((s, a) => s + a.actual, 0);
  const autoUnallocatedSurplus = Math.max(0, _rawSurplus - _totalFunded);
  const spareIncomeForSimulator =
    budgetSettings.spareIncomeAvailableForInvesting ??
    autoUnallocatedSurplus;

  void selectedPropertyForSale; // will be consumed by Sell/Profit simulator tab in a future task

  function addStructure(s: Structure) {
    setPortfolioStructures((prev) => [...prev, s]);
    if (isSupabaseEnabled() && session) {
      addDirtyId("ppa:structures:dirty", s.id);
      void upsertStructure(s, session.user.id).then((r) => handleSaveResult("ppa:structures:dirty", s.id, r, "structure"));
    }
  }

  function updateStructure(s: Structure) {
    setPortfolioStructures((prev) => prev.map((st) => (st.id === s.id ? s : st)));
    if (isSupabaseEnabled() && session) {
      addDirtyId("ppa:structures:dirty", s.id);
      void upsertStructure(s, session.user.id).then((r) => handleSaveResult("ppa:structures:dirty", s.id, r, "structure"));
    }
  }

  // B23 — Sweep all uploaded files for a structure from profile-documents
  // bucket before deleting the DB row. Best-effort; logs and continues on
  // failure so the user can still delete the structure even if storage
  // cleanup hits a transient error.
  async function sweepStructureStorage(userId: string, structureId: string): Promise<void> {
    if (!isSupabaseEnabled() || !supabase) return;
    const prefix = `${userId}/structures/${structureId}`;
    try {
      const { data: files, error } = await supabase.storage
        .from("profile-documents")
        .list(prefix, { limit: 1000 });
      if (error) {
        console.error("[sweepStructureStorage] list failed:", error.message);
        return;
      }
      if (!files || files.length === 0) return;
      const paths = files.filter((f) => f.name).map((f) => `${prefix}/${f.name}`);
      if (paths.length === 0) return;
      const { error: removeError } = await supabase.storage
        .from("profile-documents")
        .remove(paths);
      if (removeError) console.error("[sweepStructureStorage] remove failed:", removeError.message);
    } catch (err) {
      console.error("[sweepStructureStorage]", err);
    }
  }

  // B23 — Sweep all uploaded files for a property from property-documents
  // bucket. Layout has files at three locations:
  //   1. {userId}/{propertyId}/<filename>            — PropertyDocuments writes here
  //   2. {userId}/{propertyId}/contract/<filename>   — handleSectionUpload (PM)
  //   3. {userId}/{propertyId}/lease/<filename>      — handleSectionUpload (lease)
  //   4. {userId}/{propertyId}/other/<filename>      — DocumentsPanel
  // Supabase JS list() is non-recursive so we sweep the root + each known
  // tag subfolder. Best-effort throughout.
  async function sweepPropertyStorage(userId: string, propertyId: string): Promise<void> {
    if (!isSupabaseEnabled() || !supabase) return;
    const rootPrefix = `${userId}/${propertyId}`;
    const subfolders = ["contract", "lease", "other"];
    try {
      // Sweep each subfolder
      for (const sub of subfolders) {
        const subPrefix = `${rootPrefix}/${sub}`;
        const { data: subFiles, error: subErr } = await supabase.storage
          .from("property-documents")
          .list(subPrefix, { limit: 1000 });
        if (subErr) {
          console.error(`[sweepPropertyStorage] list ${subPrefix} failed:`, subErr.message);
          continue;
        }
        if (subFiles && subFiles.length > 0) {
          const subPaths = subFiles.filter((f) => f.name).map((f) => `${subPrefix}/${f.name}`);
          if (subPaths.length > 0) {
            const { error: rmErr } = await supabase.storage
              .from("property-documents")
              .remove(subPaths);
            if (rmErr) console.error(`[sweepPropertyStorage] remove ${subPrefix} failed:`, rmErr.message);
          }
        }
      }
      // Sweep root level (PropertyDocuments-flat files)
      const { data: rootFiles, error: rootErr } = await supabase.storage
        .from("property-documents")
        .list(rootPrefix, { limit: 1000 });
      if (rootErr) {
        console.error(`[sweepPropertyStorage] list ${rootPrefix} failed:`, rootErr.message);
        return;
      }
      if (rootFiles && rootFiles.length > 0) {
        // Filter to actual files only (folders are entries with no id/metadata)
        const rootPaths = rootFiles
          .filter((f) => f.name && f.id)
          .map((f) => `${rootPrefix}/${f.name}`);
        if (rootPaths.length > 0) {
          const { error: rmErr } = await supabase.storage
            .from("property-documents")
            .remove(rootPaths);
          if (rmErr) console.error(`[sweepPropertyStorage] remove root failed:`, rmErr.message);
        }
      }
    } catch (err) {
      console.error("[sweepPropertyStorage]", err);
    }
  }

  function deleteStructure(id: string) {
    const inUse = portfolioProperties.some((p) => p.structureId === id);
    if (inUse) {
      alert("Cannot delete this structure — properties are assigned to it. Reassign them first.");
      return;
    }
    setPortfolioStructures((prev) => prev.filter((st) => st.id !== id));
    if (isSupabaseEnabled() && session) {
      // Best-effort storage sweep before DB delete (B23)
      void sweepStructureStorage(session.user.id, id);
      void deleteSupabaseStructure(id);
    }
  }

  function commitScenarioDrafts(drafts: ScenarioDraftProperty[]) {
    const mapped = drafts.map((draft, index) =>
      mapScenarioDraftToProperty(draft, index)
    );

    let toUpsert: PropertyRecord[] = [];
    setPortfolioProperties((prev) => {
      const existingDraftNames = new Set(
        prev
          .filter((p) => p.notes?.includes("[DRAFT SCENARIO]"))
          .map((p) => p.nickname)
      );

      const newDrafts = mapped.filter(
        (draft) => !existingDraftNames.has(draft.nickname)
      );
      toUpsert = newDrafts;
      return [...prev, ...newDrafts];
    });

    if (isSupabaseEnabled() && session && toUpsert.length > 0) {
      void upsertProperties(toUpsert, session.user.id).catch((err) => showGlobalSaveError(`Failed to save properties — ${(err as Error).message}`));
    }

    setCurrent("portfolio");
  }

  function deleteDraftProperty(propertyId: string) {
    setPortfolioProperties((prev) =>
      prev.filter((property) => property.id !== propertyId)
    );
    if (isSupabaseEnabled() && session) void deleteSupabaseProperty(propertyId);
  }

  function confirmDraftProperty(propertyId: string) {
    let confirmed: PropertyRecord | undefined;
    setPortfolioProperties((prev) =>
      prev.map((property) => {
        if (property.id === propertyId) {
          confirmed = { ...property, notes: property.notes?.replace("[DRAFT SCENARIO] ", "") || "" };
          return confirmed;
        }
        return property;
      })
    );
    // Layer 1 null-address guard — see ARCHITECTURE §7. A draft scenario being
    // confirmed should already have an address; defensive guard catches any
    // edge case where it doesn't.
    if (confirmed && !confirmed.address?.trim()) return;
    if (isSupabaseEnabled() && session && confirmed) {
      const id = confirmed.id;
      addDirtyId("ppa:properties:dirty", id);
      void upsertProperty(confirmed, session.user.id).then((r) => handleSaveResult("ppa:properties:dirty", id, r, "property"));
    }
  }

  function updateProperty(updated: PropertyRecord) {
    // N1 — auto-snapshot value/rent history when those fields change.
    // Snapshots are deduplicated (same value as latest = no-op) and
    // same-day updates replace today's entry rather than appending.
    const previous = portfolioProperties.find((p) => p.id === updated.id);
    if (previous) {
      if (previous.currentValue !== updated.currentValue) {
        const newValueHistory = maybeAppendSnapshot(updated.valueHistory, updated.currentValue);
        if (newValueHistory) updated = { ...updated, valueHistory: newValueHistory };
      }
      if (previous.weeklyRent !== updated.weeklyRent) {
        const newRentHistory = maybeAppendSnapshot(updated.rentHistory, updated.weeklyRent);
        if (newRentHistory) updated = { ...updated, rentHistory: newRentHistory };
      }
      // N1 race guard — concurrent saves (e.g. fire-and-forget geocoding
      // callback in PropertyDetailPage) can construct payloads from stale
      // draftRef.current that lacks the history App.tsx just added. Never
      // let an incoming save shrink the history arrays — only extend.
      const prevValueLen = (previous.valueHistory ?? []).length;
      const newValueLen = (updated.valueHistory ?? []).length;
      if (newValueLen < prevValueLen) {
        updated = { ...updated, valueHistory: previous.valueHistory };
      }
      const prevRentLen = (previous.rentHistory ?? []).length;
      const newRentLen = (updated.rentHistory ?? []).length;
      if (newRentLen < prevRentLen) {
        updated = { ...updated, rentHistory: previous.rentHistory };
      }
    }
    setPortfolioProperties((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p))
    );
    // Layer 1 null-address guard — see ARCHITECTURE §7. Local state still
    // accepts the update (so the user can keep editing the blank property
    // detail page) but Supabase persistence is deferred until an address is
    // entered. Layer 2 in supabaseStorage.upsertProperty is the backstop.
    if (!updated.address?.trim()) return;
    if (isSupabaseEnabled() && session) {
      addDirtyId("ppa:properties:dirty", updated.id);
      void upsertProperty(updated, session.user.id).then((r) => handleSaveResult("ppa:properties:dirty", updated.id, r, "property"));
    }
  }

  function handleAddBlankProperty() {
    const newId = crypto.randomUUID();
    const blank: PropertyRecord = {
      id: newId,
      nickname: "New property",
      address: "",
      suburb: "",
      state: "",
      postcode: "",
      assetClass: "Residential",
      propertyType: "House",
      status: "active",
      purchaseDate: "",
      purchasePrice: 0,
      buyCosts: 0,
      legalBuyCosts: 0,
      currentValue: 0,
      weeklyRent: 0,
      loanBalance: 0,
      structureId: "",
      owner: "",
      lender: "",
      loanAccountNumber: "",
      offsetAccount: "",
      loanStartDate: "",
      loanTermYears: 30,
      ioTermYears: 0,
      rate: 0,
      repaymentType: "P&I",
      rateType: "Variable",
      monthlyExpenses: 0,
      annualRates: 0,
      annualInsurance: 0,
      annualStrata: 0,
      annualLandTax: 0,
      annualMaintenance: 0,
      annualPropertyManagement: 0,
      personalTopUp: 0,
      estimatedSellingCostsPct: 2.5,
      customExpenses: [],
      customTabs: [],
      notes: "",
    };
    setPortfolioProperties(prev => [...prev, blank]);
    // Don't save to Supabase yet — the normal onBlur save in PropertyDetailPage
    // will persist once the user enters an address
    setSelectedPropertyId(newId);
    setCurrent("property-detail");
  }

  async function handleImport(properties: PropertyRecord[], structures: Omit<Structure, "id">[], updateIds: Set<string>): Promise<{success: boolean; error?: string}> {
    // Filter out properties with no address — prevents ghost records from blank wizard rows
    const validProperties = properties.filter(p => p.address && p.address.trim().length > 0);

    // Match extracted structures against existing ones by displayName (case-insensitive)
    // to avoid creating duplicates
    const newStructures: Structure[] = [];
    for (const s of structures) {
      const existing = portfolioStructures.find(
        (es) => es.displayName.toLowerCase().trim() === s.displayName.toLowerCase().trim()
      );
      if (!existing) {
        newStructures.push({ ...s, id: crypto.randomUUID() });
      }
      // If existing match found, skip — structure already exists for this user
    }

    // Link properties to structures by entityName (carried via __entity: prefix in owner field)
    const allStructures = [...portfolioStructures, ...newStructures];
    for (const p of validProperties) {
      if (p.owner?.startsWith("__entity:")) {
        const entityName = p.owner.slice("__entity:".length);
        const matched = allStructures.find(
          (s) => s.displayName.toLowerCase().trim() === entityName.toLowerCase().trim()
        );
        if (matched) {
          p.structureId = matched.id;
        }
        p.owner = ""; // Clear the temporary carrier field
      }
    }

    // Build address → existing record map for duplicate detection
    const existingAddressMap = new Map(
      portfolioProperties.map((p) => [(p.address ?? "").trim().toLowerCase(), p])
    );

    // Split into inserts (new addresses) and updates (duplicate addresses user chose to overwrite)
    const toInsert: PropertyRecord[] = [];
    const toUpdate: PropertyRecord[] = [];
    for (const p of validProperties) {
      const addrKey = (p.address ?? "").trim().toLowerCase();
      if (existingAddressMap.has(addrKey)) {
        if (updateIds.has(p.id)) {
          // Use the existing portfolio record's id so the upsert overwrites correctly
          const existing = existingAddressMap.get(addrKey)!;
          toUpdate.push({ ...p, id: existing.id });
        }
        // else skip duplicate silently
      } else {
        toInsert.push(p);
      }
    }

    // Enforce property limit — trial users get full access, read-only users blocked
    if (isReadOnly) { setCurrent("billing"); return { success: false, error: "Subscribe to continue." }; }
    if (isSupabaseEnabled() && !hasFullAccess) {
      const currentCommitted = portfolioProperties.filter(
        (p) => !p.notes?.includes("[DRAFT SCENARIO]")
      ).length;
      const newCommitted = toInsert.filter(
        (p) => !p.notes?.includes("[DRAFT SCENARIO]")
      ).length;
      if (currentCommitted + newCommitted > 2) {
        openUpgradeModal("property_limit");
        return { success: false, error: "Upgrade to Investor to import more than 2 properties." };
      }
    }

    setPortfolioProperties((prev) => {
      const updated = prev.map((existing) => {
        const match = toUpdate.find((u) => u.id === existing.id);
        return match ?? existing;
      });
      return [...updated, ...toInsert];
    });
    if (newStructures.length > 0) {
      setPortfolioStructures((prev) => [...prev, ...newStructures]);
    }
    if (isSupabaseEnabled() && session) {
      try {
        if (newStructures.length > 0) await upsertStructures(newStructures, session.user.id);
        const allToSave = [...toInsert, ...toUpdate];
        if (allToSave.length > 0) await upsertProperties(allToSave, session.user.id);
        // Show brief cloud-sync confirmation banner
        setCloudSyncBanner(true);
        setTimeout(() => setCloudSyncBanner(false), 4000);
      } catch (err) {
        console.error("[handleImport] Supabase error:", err);
        return { success: false, error: "Failed to save to cloud. Check your connection and try again." };
      }
    }
    return { success: true };
  }

  function handleImportBudget(budget: ExtractedBudget, mode: 'replace' | 'merge') {
    const newIncomeRows: IncomeRow[] = budget.income.map((inc) => ({
      id: crypto.randomUUID(),
      label: inc.label,
      amount: inc.amount,
      frequency: inc.frequency,
    }));
    const miscGroupId = budgetSettings.groups.find((g) => g.name === "Miscellaneous")?.id ?? "g-12";
    const newCategories: BudgetCategory[] = budget.categories.map((cat) => ({
      id: crypto.randomUUID(),
      category: cat.category,
      groupId: budgetSettings.groups.find((g) => g.name === cat.groupName)?.id ?? miscGroupId,
      budget: cat.budget,
      actual: 0,
      frequency: cat.frequency,
    }));

    if (mode === 'replace') {
      setBudgetCategories(newCategories);
      if (newIncomeRows.length > 0) {
        setBudgetSettings((prev) => ({ ...prev, incomeRows: newIncomeRows }));
      }
    } else {
      // Merge: only add categories/income rows not already present by name
      setBudgetCategories((prev) => {
        const existingNames = new Set(prev.map((c) => c.category.trim().toLowerCase()));
        const toAdd = newCategories.filter((c) => !existingNames.has(c.category.trim().toLowerCase()));
        return [...prev, ...toAdd];
      });
      if (newIncomeRows.length > 0) {
        setBudgetSettings((prev) => {
          const existingLabels = new Set((prev.incomeRows ?? []).map((r) => r.label.trim().toLowerCase()));
          const toAdd = newIncomeRows.filter((r) => !existingLabels.has(r.label.trim().toLowerCase()));
          return { ...prev, incomeRows: [...(prev.incomeRows ?? []), ...toAdd] };
        });
      }
    }
  }

  function selectProperty(propertyId: string) {
    setSelectedPropertyId(propertyId);
    setCurrent("property-detail");
  }

  function handleExportToSell(propertyId: string) {
    console.log("Export to Sell/Profit:", propertyId);
    setSelectedPropertyForSale(propertyId);
    setCurrent("simulator");
  }

  function handleDeleteProperty(propertyId: string) {
    setPortfolioProperties((prev) => prev.filter((p) => p.id !== propertyId));
    if (isSupabaseEnabled() && session) {
      // Best-effort storage sweep before DB delete (B23)
      void sweepPropertyStorage(session.user.id, propertyId);
      void deleteSupabaseProperty(propertyId);
    }
  }

  async function handleDeleteProperties() {
    try {
      if (isSupabaseEnabled() && supabase && session) {
        await supabase.from("properties").delete().eq("user_id", session.user.id);
        await supabase.from("structures").delete().eq("user_id", session.user.id);
      }
    } catch (err) {
      console.error("[handleDeleteProperties]", err);
    }
    localStorage.removeItem("ppa:properties");
    localStorage.removeItem("ppa:structures");
    setPortfolioProperties([]);
    setPortfolioStructures([]);
  }

  async function handleDeleteBudget() {
    try {
      if (isSupabaseEnabled() && supabase && session) {
        await supabase.from("budget").delete().eq("user_id", session.user.id);
      }
    } catch (err) {
      console.error("[handleDeleteBudget]", err);
    }
    localStorage.removeItem("ppa:budget");
    setBudgetSettings(seedBudgetSettings);
    setBudgetCategories(seedBudgetCategories);
  }

  async function handleDeleteProfile() {
    try {
      if (isSupabaseEnabled() && supabase && session) {
        await supabase.from("profiles").delete().eq("user_id", session.user.id);
      }
    } catch (err) {
      console.error("[handleDeleteProfile]", err);
    }
    localStorage.removeItem("ppa:profile");
    setProfile(loadProfile());
  }

  function confirmPropertySale(propertyId: string) {
    setPortfolioProperties((prev) => prev.filter((p) => p.id !== propertyId));
    if (selectedPropertyForSale === propertyId) setSelectedPropertyForSale(null);
    setCurrent("portfolio");
    if (isSupabaseEnabled() && session) {
      // Best-effort storage sweep before DB delete (B23)
      void sweepPropertyStorage(session.user.id, propertyId);
      void deleteSupabaseProperty(propertyId);
    }
  }

  const activeProperties = portfolioProperties.filter(
    (p) => !p.status || p.status === "active"
  );

  const setupComplete = {
    profile: (profile.annualPreTaxIncome ?? 0) > 0,
    portfolio: activeProperties.filter((p) => !p.notes?.includes("[DRAFT SCENARIO]")).length > 0,
    loans: activeProperties.some((p) => (p.loanBalance ?? 0) > 0),
    budget: (budgetSettings.incomeRows ?? []).length > 0,
    goals: (profile.passiveIncomeTarget ?? 0) > 0,
  };

  const computedMonthlySurplus = computedMonthlyIncome - computedMonthlyExpenses;
  const portfolioContext = buildPortfolioContext(effectiveGoalProfile, activeProperties, portfolioStructures, appSettings.includeSMSFInPortfolio, budgetSettings, appSettings, computedMonthlySurplus);

  // Public routes — no auth required
  const path = window.location.pathname;
  if (path === "/terms") return <TermsPage />;
  if (path === "/privacy") return <PrivacyPage />;

  // Auth gate — only when Supabase is configured
  if (isSupabaseEnabled()) {
    if (sessionLoading) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#f7f7f4]">
          <div className="h-6 w-6 rounded-full border-2 border-neutral-300 border-t-neutral-900 animate-spin" />
        </div>
      );
    }
    // PASSWORD_RECOVERY: force user to set new password before app render.
    if (recoveryMode) {
      return <SetPasswordPage onComplete={() => setRecoveryMode(false)} />;
    }
    if (!session) {
      const hostname = window.location.hostname;
      const params = new URLSearchParams(window.location.search);
      const wantsAuth = params.get("auth") === "1";
      if (!wantsAuth && (hostname === "ausprop.app" || hostname === "www.ausprop.app" || hostname === "propertyos.com.au" || hostname === "www.propertyos.com.au" || hostname === "localhost" || hostname === "127.0.0.1")) return <Suspense fallback={null}><LandingPage /></Suspense>;
      return <AuthPage />;
    }
  }

  return (
    <div className="flex h-screen bg-[#f7f7f4]">
      <Sidebar current={current} setCurrent={setCurrent} adminEmail={session?.user.email ?? undefined} setupComplete={setupComplete} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Share accepted banner */}
        {shareAcceptedBanner && (
          <div className="mx-4 mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="text-sm font-medium text-emerald-800">{shareAcceptedBanner}</span>
            <button onClick={() => setShareAcceptedBanner(null)} className="ml-auto text-emerald-600 hover:text-emerald-800 text-lg leading-none">×</button>
          </div>
        )}
        {/* Trial banner */}
        {isSupabaseEnabled() && session && isInTrial && (
          <div className="mx-4 mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3">
            <span className="text-sm text-amber-800">
              <strong>Free trial:</strong> {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} remaining — full access to all features.
            </span>
            <button
              onClick={() => setCurrent("billing" as Parameters<typeof setCurrent>[0])}
              className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition-colors"
            >
              Subscribe →
            </button>
          </div>
        )}
        {/* Read-only banner — trial expired */}
        {isSupabaseEnabled() && session && isReadOnly && (
          <div className="mx-4 mt-3 rounded-xl bg-neutral-900 px-4 py-3 flex items-center justify-between gap-3">
            <span className="text-sm text-white">
              <strong>Your trial has ended.</strong> Your data is safe — subscribe to continue editing.
            </span>
            <button
              onClick={() => void handleDirectCheckout()}
              className="shrink-0 rounded-lg bg-[#C47A2F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#A8682A] transition-colors"
            >
              Subscribe — $39/month →
            </button>
          </div>
        )}
        {/* Cloud sync banner */}
        {cloudSyncBanner && (
          <div className="mx-4 mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="text-sm font-medium text-emerald-800">Portfolio saved to cloud ✓</span>
            <button onClick={() => setCloudSyncBanner(false)} className="ml-auto text-emerald-600 hover:text-emerald-800 text-lg leading-none">×</button>
          </div>
        )}
        {budgetSavedBanner && current === "budget" && (
          <div className="mx-4 mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-emerald-700">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
            Budget saved to cloud ✓
          </div>
        )}
        {budgetSaveError && current === "budget" && (
          <div className="mx-4 mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-red-700">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
            Failed to save budget — changes are saved locally and will retry
            <button onClick={() => setBudgetSaveError(null)} className="ml-auto text-red-500 hover:text-red-700 text-lg leading-none">×</button>
          </div>
        )}
        {globalSaveError && (
          <div className="mx-4 mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-red-700">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
            {globalSaveError}
            <button onClick={() => setGlobalSaveError(null)} className="ml-auto text-red-500 hover:text-red-700 text-lg leading-none">×</button>
          </div>
        )}
        {isDemoMode && (
          <div className="sticky top-0 z-20 flex items-center justify-between px-6 py-2.5" style={{ background: '#D97706' }}>
            <span className="text-sm font-semibold text-white">You're viewing sample portfolio data. This is not your real portfolio.</span>
            <button
              onClick={exitDemoMode}
              className="rounded-lg bg-white/20 px-4 py-1.5 text-sm font-semibold text-white hover:bg-white/30 transition-colors"
            >
              Return to my portfolio →
            </button>
          </div>
        )}
        {isSupabaseEnabled() && session && (
          <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-0">
            {/* Portfolio switcher shell — shared portfolio loading is Phase 2 (requires RLS changes + portfolio-view Edge Function) */}
            <div className="flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              Viewing: My Portfolio
            </div>
            <UserMenu email={session.user.email ?? ""} />
          </div>
        )}
      <div className="flex-1 overflow-y-auto p-4 pb-24 md:p-10 md:pb-10">
        <Suspense fallback={PageSpinner}>
        <div className="mx-auto max-w-7xl rounded-2xl md:rounded-[2rem] bg-white p-4 md:p-8 shadow-xl">
          {current === "dashboard" && (
            <DashboardPage
              profile={effectiveGoalProfile}
              portfolioProperties={activeProperties}
              portfolioStructures={portfolioStructures}
              budgetSettings={budgetSettings}
              budgetCategories={budgetCategories}
              onNavigate={(page) => setCurrent(page as Page)}
              onSelectProperty={(id) => {
                setSelectedPropertyId(id);
                setCurrent("property-detail");
              }}
              setupComplete={setupComplete}
              subscriptionTier={subscriptionTier}
              userId={session?.user.id}
              appSettings={appSettings}
            />
          )}
          {current === "profile" && (
            <ProfilePage
              profile={profile}
              structures={portfolioStructures}
              portfolioProperties={portfolioProperties}
              onSave={updateProfile}
              onAddStructure={addStructure}
              onUpdateStructure={updateStructure}
              onDeleteStructure={deleteStructure}
            />
          )}
          {current === "budget" && (
            <BudgetPage
              settings={budgetSettings}
              categories={budgetCategories}
              profile={profile}
              onUpdateSettings={updateBudgetSettings}
              onUpdateCategories={updateBudgetCategories}
              onOpenImport={(mode) => { setImportWizardMode(mode); setImportWizardOpen(true); }}
              lastSaved={budgetLastSaved}
              appSettings={appSettings}
              onUpdateAppSettings={(s) => {
                setAppSettings(s);
                saveAppSettings(s);
                if (isSupabaseEnabled() && session) {
                  addDirtyId("ppa:appSettings:dirty", null);
                  void upsertAppSettings(s, session.user.id).then((r) => handleSaveResult("ppa:appSettings:dirty", null, r, "settings"));
                }
              }}
            />
          )}
          {current === "portfolio" && (
            <PortfolioPage
              portfolioProperties={portfolioProperties}
              portfolioStructures={portfolioStructures}
              profile={profile}
              subscriptionTier={subscriptionTier}
              onDeleteDraft={deleteDraftProperty}
              onConfirmDraft={confirmDraftProperty}
              onUpdateDraft={updateProperty}
              onSelectProperty={selectProperty}
              onGoToSimulator={() => setCurrent("simulator")}
              onNavigateToProfile={() => setCurrent("profile")}
              onOpenImport={() => setImportWizardOpen(true)}
              onAddProperty={handleAddBlankProperty}
              onNavigateToBilling={() => setCurrent("billing")}
              onUpdateStructure={updateStructure}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
              hasFullAccess={hasFullAccess}
            />
          )}
          {current === "property-detail" && (() => {
            const prop = portfolioProperties.find((p) => p.id === selectedPropertyId);
            if (!prop) return null;
            return (
              <PropertyDetailPage
                property={prop}
                structures={portfolioStructures}
                onSave={updateProperty}
                onBack={() => setCurrent("portfolio")}
                onExportToSell={handleExportToSell}
                onDeleteProperty={handleDeleteProperty}
                investorMarginalRate={profile.marginalTaxRate ?? 32.5}
                profile={profile}
                userId={session?.user.id}
                subscriptionTier={subscriptionTier}
                onNavigateToBilling={() => setCurrent("billing")}
                onNavigateToProfile={() => setCurrent("profile")}
              />
            );
          })()}
          {current === "loans" && (
            <LoansPage
              portfolioProperties={activeProperties}
              onUpdateProperty={updateProperty}
              portfolioStructures={portfolioStructures}
              onNavigate={(page) => setCurrent(page as Parameters<typeof setCurrent>[0])}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
            />
          )}
          {current === "transfers" && (
            <TransfersPage
              budgetSettings={budgetSettings}
              portfolioProperties={portfolioProperties}
              portfolioStructures={portfolioStructures}
              appSettings={appSettings}
              profile={profile}
              onUpdateStructure={updateStructure}
              onUpdateProperty={updateProperty}
              onNavigateToLoans={() => setCurrent("loans")}
              onNavigateToTax={() => setCurrent("tax")}
              onUpdateAppSettings={(s) => {
                setAppSettings(s);
                saveAppSettings(s);
                if (isSupabaseEnabled() && session) {
                  addDirtyId("ppa:appSettings:dirty", null);
                  void upsertAppSettings(s, session.user.id).then((r) => handleSaveResult("ppa:appSettings:dirty", null, r, "settings"));
                }
              }}
              monthlySurplus={computedMonthlyIncome - computedMonthlyExpenses}
              userEmail={session?.user.email ?? undefined}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
            />
          )}
          {current === "simulator" && (
            <SimulatorPage
              onCommitScenarioDrafts={commitScenarioDrafts}
              spareIncomeAvailableForInvesting={spareIncomeForSimulator}
              portfolioStructures={portfolioStructures}
              selectedPropertyForSale={activeProperties.find((p) => p.id === selectedPropertyForSale) ?? undefined}
              portfolioProperties={activeProperties}
              investorMarginalRate={profile.marginalTaxRate ?? 32.5}
              profile={profile}
              onConfirmSale={confirmPropertySale}
              defaultTab={simulatorDefaultTab}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
              budgetSettings={budgetSettings}
              onUpdateBudgetSettings={updateBudgetSettings}
            />
          )}
          {current === "strategy" && (
            <StrategyPage
              profile={effectiveGoalProfile}
              portfolioProperties={activeProperties}
              portfolioStructures={portfolioStructures}
              budgetSettings={budgetSettings}
              investorMarginalRate={profile.marginalTaxRate ?? 32.5}
              maxLVR={appSettings.lvrStandard}
              onUpdateProfile={updateProfile}
              isSecondaryViewer={!!ownerProfile}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
            />
          )}
          {current === "reports" && (
            <ReportsPage
              onNavigate={(page, simulatorTab) => {
                if (simulatorTab) setSimulatorDefaultTab(simulatorTab);
                setCurrent(page as Page);
              }}
              portfolioProperties={portfolioProperties}
              profile={profile}
              portfolioStructures={portfolioStructures}
              includeSMSFInPortfolio={appSettings.includeSMSFInPortfolio}
            />
          )}
          {current === "tax" && (
            <TaxPage
              portfolioProperties={portfolioProperties}
              portfolioStructures={portfolioStructures}
              profile={profile}
              onNavigate={(page) => setCurrent(page as Parameters<typeof setCurrent>[0])}
              onUpdateProperty={updateProperty}
              onUpdateProfile={updateProfile}
              userId={session?.user.id}
            />
          )}
          {current === "billing" && (
            <BillingPage subscriptionTier={subscriptionTier} userId={session?.user.id} isInTrial={isInTrial} trialDaysLeft={trialDaysLeft} trialExpired={isReadOnly} />
          )}
          {current === "admin" && session?.user.email === "anguswilliams76@gmail.com" && (
            <AdminPage onNavigate={(p) => setCurrent(p as Page)} />
          )}
          {current === "settings" && (
            <SettingsPage
              settings={appSettings}
              onSave={(s) => {
                setAppSettings(s);
                saveAppSettings(s);
                if (isSupabaseEnabled() && session) {
                  addDirtyId("ppa:appSettings:dirty", null);
                  void upsertAppSettings(s, session.user.id).then((r) => handleSaveResult("ppa:appSettings:dirty", null, r, "settings"));
                }
              }}
              subscriptionTier={subscriptionTier}
              onOpenImport={() => setImportWizardOpen(true)}
              onNavigateToBilling={() => setCurrent("billing")}
              userEmail={session?.user.email ?? undefined}
              userId={session?.user.id}
              userName={profile?.preferredName ?? profile?.fullLegalName ?? session?.user.email}
              onDeleteProperties={handleDeleteProperties}
              onDeleteBudget={handleDeleteBudget}
              onDeleteProfile={handleDeleteProfile}
              hasFullAccess={hasFullAccess}
            />
          )}
        </div>
        </Suspense>
      </div>
      </div>

      {/* AI Assistant — locked when no full access (trial expired / never subscribed) */}
      {isSupabaseEnabled() && !hasFullAccess ? (
        <button
          onClick={() => openUpgradeModal(isReadOnly ? "trial_expired" : "ai_feature")}
          className="fixed bottom-20 right-4 z-40 md:bottom-6 md:right-6 flex items-center gap-2 rounded-full bg-neutral-400 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-neutral-500 transition-colors"
          title="AI features require the Investor plan"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Ask AI
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </button>
      ) : (
        <button
          onClick={() => setAiDrawerOpen(true)}
          className="fixed bottom-20 right-4 z-40 md:bottom-6 md:right-6 flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-neutral-700 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Ask AI
        </button>
      )}
      <AIChatDrawer
        isOpen={aiDrawerOpen}
        onClose={() => setAiDrawerOpen(false)}
        portfolioContext={portfolioContext}
        currentPage={current}
        onUpgradeRequired={() => openUpgradeModal("ai_feature")}
      />
      {/* EmptyState: Phase 2 brand-new authenticated users with no properties */}
      {isSupabaseEnabled() && !!session && !recoveryMode && dataLoaded && portfolioProperties.length === 0 && !onboarded && (
        <EmptyState
          profile={profile}
          onOpenImport={handleEmptyStateImport}
          onAddManually={handleEmptyStateAddManually}
          onLoadDemo={handleEmptyStateLoadDemo}
        />
      )}
      {/* OnboardingWizard: Phase 1 (always), Phase 2 users who gained properties before completing setup */}
      {!onboarded && !recoveryMode && (portfolioProperties.length > 0 || !isSupabaseEnabled()) && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          onOpenImport={() => setImportWizardOpen(true)}
        />
      )}
      {importWizardOpen && (
        <ImportWizard
          onImport={handleImport}
          onImportBudget={handleImportBudget}
          onClose={() => setImportWizardOpen(false)}
          onNavigate={(p) => { setImportWizardOpen(false); setCurrent(p as Page); }}
          existingAddresses={new Set(portfolioProperties.map((p) => (p.address ?? "").trim().toLowerCase()))}
          importMode={importWizardMode}
        />
      )}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onNavigateToBilling={() => { setUpgradeModalOpen(false); setCurrent("billing"); }}
        reason={upgradeModalReason}
        userId={session?.user.id}
      />
      <BottomNav current={current} setCurrent={setCurrent} hasPortfolio={setupComplete.portfolio} hasGoals={setupComplete.goals} />
    </div>
  );
}

function _Placeholder({ title }: { title: string }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="mt-2 text-neutral-600">Coming next...</p>
    </div>
  );
}
void _Placeholder;